import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { getCompetitorBySlug, STIB_BASE, ALL_COMPETITORS } from '../data';

function ComparisonBar({ label, stibScore, competitorScore, stibLabel, competitorLabel }: {
  label: string;
  stibScore: number;
  competitorScore: number;
  stibLabel: string;
  competitorLabel: string;
}) {
  const max = Math.max(stibScore, competitorScore, 1);
  const stibPct = (stibScore / max) * 100;
  const compPct = (competitorScore / max) * 100;

  return (
    <div className="space-y-3">
      <p className="font-body font-semibold text-secondary text-sm">{label}</p>
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <span className="font-body text-xs text-primary font-bold w-12 flex-shrink-0 text-right">STIB</span>
          <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-1000" style={{ width: `${stibPct}%` }} />
          </div>
          <span className="font-body text-xs text-gray-500 w-16 flex-shrink-0">{stibLabel}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-body text-xs text-gray-400 font-bold w-12 flex-shrink-0 text-right">VS</span>
          <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
            <div className="h-full bg-gray-300 rounded-full transition-all duration-1000" style={{ width: `${compPct}%` }} />
          </div>
          <span className="font-body text-xs text-gray-500 w-16 flex-shrink-0">{competitorLabel}</span>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ question, answer, index }: { question: string; answer: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden" itemScope itemType="https://schema.org/Question">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-7 py-5 bg-white hover:bg-gray-50 transition-colors cursor-pointer text-left"
        aria-expanded={open}
        id={`faq-compare-${index}`}
      >
        <h4 className="font-body font-semibold text-secondary text-base leading-snug pr-4" itemProp="name">{question}</h4>
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-primary/10">
          <i className={`ri-${open ? 'subtract' : 'add'}-line text-primary text-base`}></i>
        </div>
      </button>
      {open && (
        <div className="px-7 pb-6 bg-white" itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
          <p className="font-body text-gray-600 text-sm leading-relaxed" itemProp="text">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function ComparisonPage() {
  const { slug } = useParams<{ slug: string }>();
  const competitor = getCompetitorBySlug(slug || '');

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: verdictRef, isVisible: verdictVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: tableRef, isVisible: tableVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: prosRef, isVisible: prosVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: barsRef, isVisible: barsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.1 });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

  useEffect(() => {
    if (!competitor) return;

    document.title = competitor.metaTitle;

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', competitor.metaDescription);

    const schemas = [
      {
        id: 'compare-organization',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: STIB_BASE.fullName,
          alternateName: STIB_BASE.name,
          url: siteUrl,
          description: STIB_BASE.keyDifferentiator,
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'São Paulo',
            addressRegion: 'SP',
            addressCountry: 'BR',
          },
        },
      },
      {
        id: 'compare-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Comparativos', item: `${siteUrl}/comparativo` },
            { '@type': 'ListItem', position: 3, name: `${competitor.name}`, item: `${siteUrl}/comparativo/${competitor.slug}` },
          ],
        },
      },
      {
        id: 'compare-faq',
        data: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: competitor.faqs.map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: { '@type': 'Answer', text: f.answer },
          })),
        },
      },
    ];

    schemas.forEach(({ id, data }) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const s = document.createElement('script');
      s.id = id;
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [competitor, siteUrl]);

  if (!competitor) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-40 text-center mt-[72px]">
          <h1 className="font-heading font-bold text-4xl text-secondary mb-6">Comparativo não encontrado</h1>
          <p className="font-body text-gray-500 mb-8">Este comparativo não existe. Confira os comparativos disponíveis.</p>
          <Link to="/comparativo" className="inline-block bg-primary text-white px-8 py-3 rounded-full font-body font-semibold hover:bg-opacity-90 transition-all whitespace-nowrap cursor-pointer">
            Ver todos os comparativos
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const stibScores = {
    geoCoverage: 8,
    specialization: 9,
    equipment: 8,
    experience: 5,
    service: 7,
    price: 8,
    tech: 7,
    support: 7,
  };

  const stibTotal = Object.values(stibScores).reduce((a, b) => a + b, 0);
  const compTotal = competitor.geoCoverageScore + competitor.specializationScore + competitor.equipmentScore + competitor.experienceScore + competitor.serviceScore + competitor.priceScore + competitor.techScore + competitor.supportScore;

  const winner = stibTotal > compTotal ? 'stib' : compTotal > stibTotal ? 'competitor' : 'tie';

  return (
    <>
      <Header />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative min-h-[70vh] w-full flex items-center justify-center overflow-hidden mt-[72px]"
      >
        <div className="absolute inset-0">
          <img
            src="/img/stib/fone-kit-maleta.webp"
            alt={`STIB vs ${competitor.name} — Comparativo`}
            className={`w-full h-full object-cover object-center transition-all duration-1000 ${heroVisible ? 'scale-100 opacity-100' : 'scale-105 opacity-0'}`}
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/50 to-black/80"></div>
        </div>

        <div className="absolute top-24 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-white/60 text-xs font-body">
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-white/40"></i>
              <Link to="/comparativo" className="hover:text-white transition-colors cursor-pointer">Comparativos</Link>
              <i className="ri-arrow-right-s-line text-white/40"></i>
              <span className="text-white/80">{competitor.name}</span>
            </nav>
          </div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto w-full">
          <div className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold uppercase tracking-widest px-5 py-2 rounded-full mb-8 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <i className="ri-scales-3-line text-primary text-sm"></i>
            comparativo detalhado
          </div>

          <header>
            <h1 className={`font-heading font-extrabold text-4xl md:text-6xl text-white mb-6 tracking-tight leading-tight lowercase transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              stib vs.<br />
              <span className="text-primary">{competitor.name.toLowerCase()}</span>
            </h1>
          </header>

          <p className={`font-body font-light text-lg md:text-xl text-white/80 mb-10 max-w-3xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {competitor.description}
          </p>

          <div className={`flex flex-wrap items-center justify-center gap-6 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="text-center">
              <p className="font-heading font-bold text-primary text-2xl">{STIB_BASE.yearsInMarket} anos</p>
              <p className="font-body text-white/60 text-xs">STIB</p>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div className="text-center">
              <p className="font-heading font-bold text-white/80 text-2xl">{competitor.yearsInMarket} anos</p>
              <p className="font-body text-white/60 text-xs">{competitor.name}</p>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div className="text-center">
              <p className="font-heading font-bold text-primary text-2xl">{stibTotal}</p>
              <p className="font-body text-white/60 text-xs">pontos STIB</p>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div className="text-center">
              <p className="font-heading font-bold text-white/80 text-2xl">{compTotal}</p>
              <p className="font-body text-white/60 text-xs">pontos {competitor.name}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── QUICK VERDICT ─────────────────────────────────────── */}
      <section ref={verdictRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Veredito em 30 segundos
            </span>
            <h2 className={`font-heading font-bold text-4xl md:text-5xl text-secondary lowercase transition-all duration-700 ${verdictVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              quem leva essa disputa?
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* STIB Card */}
            <div className={`bg-gradient-to-br from-primary/5 to-primary/10 rounded-3xl p-8 border-2 ${winner === 'stib' ? 'border-primary' : 'border-primary/20'} transition-all duration-700 ${verdictVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary">
                  <i className="ri-trophy-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-secondary text-lg">STIB</h3>
                  <p className="font-body text-xs text-primary font-semibold">{winner === 'stib' ? 'Vencedor' : 'Competitiva'}</p>
                </div>
              </div>
              <p className="font-body text-sm text-gray-600 leading-relaxed mb-6">
                {STIB_BASE.keyDifferentiator}
              </p>
              <div className="space-y-2">
                {STIB_BASE.yearsInMarket > 0 && (
                  <div className="flex items-center gap-2">
                    <i className="ri-check-line text-primary text-sm"></i>
                    <span className="font-body text-sm text-gray-600">{STIB_BASE.eventsCount} eventos realizados</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-primary text-sm"></i>
                  <span className="font-body text-sm text-gray-600">{STIB_BASE.equipmentCount} fones em estoque</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-primary text-sm"></i>
                  <span className="font-body text-sm text-gray-600">{STIB_BASE.geoCoverage}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-check-line text-primary text-sm"></i>
                  <span className="font-body text-sm text-gray-600">98% satisfação dos clientes</span>
                </div>
              </div>
            </div>

            {/* VS Badge */}
            <div className="flex items-center justify-center">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-secondary">
                <span className="font-heading font-bold text-white text-2xl">VS</span>
              </div>
            </div>

            {/* Competitor Card */}
            <div className={`bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 border-2 ${winner === 'competitor' ? 'border-secondary' : 'border-gray-200'} transition-all duration-700 delay-100 ${verdictVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary/10">
                  <i className="ri-building-line text-secondary text-xl"></i>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-secondary text-lg">{competitor.name}</h3>
                  <p className="font-body text-xs text-secondary font-semibold">{winner === 'competitor' ? 'Vencedor' : 'Competitivo'}</p>
                </div>
              </div>
              <p className="font-body text-sm text-gray-600 leading-relaxed mb-6">
                {competitor.keyDifferentiator}
              </p>
              <div className="space-y-2">
                {competitor.strengths.slice(0, 4).map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <i className="ri-check-line text-secondary text-sm"></i>
                    <span className="font-body text-sm text-gray-600">{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Overall Score */}
          <div className={`mt-12 bg-gradient-to-r from-gray-50 to-white rounded-3xl p-8 border border-gray-100 transition-all duration-700 delay-200 ${verdictVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <p className="font-body text-sm text-gray-500 mb-2">Pontuação geral (máx. 80)</p>
                <div className="flex items-center gap-6">
                  <div>
                    <p className="font-heading font-bold text-primary text-4xl">{stibTotal}</p>
                    <p className="font-body text-xs text-gray-400">STIB</p>
                  </div>
                  <div className="w-px h-12 bg-gray-200"></div>
                  <div>
                    <p className="font-heading font-bold text-secondary text-4xl">{compTotal}</p>
                    <p className="font-body text-xs text-gray-400">{competitor.name}</p>
                  </div>
                </div>
              </div>
              <div className="flex-1 max-w-md">
                <div className="flex items-center gap-4 mb-2">
                  <span className="font-body text-xs text-primary font-bold w-10">STIB</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${(stibTotal / 80) * 100}%` }} />
                  </div>
                  <span className="font-body text-xs text-gray-500 w-10">{Math.round((stibTotal / 80) * 100)}%</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-body text-xs text-secondary font-bold w-10">VS</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                    <div className="h-full bg-gray-300 rounded-full" style={{ width: `${(compTotal / 80) * 100}%` }} />
                  </div>
                  <span className="font-body text-xs text-gray-500 w-10">{Math.round((compTotal / 80) * 100)}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── COMPARISON TABLE ────────────────────────────────── */}
      <section ref={tableRef} className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Comparação Lado a Lado
            </span>
            <h2 className={`font-heading font-bold text-4xl md:text-5xl text-secondary lowercase transition-all duration-700 ${tableVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              critério por critério
            </h2>
          </div>

          <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
            <table className="w-full" role="table" aria-label={`Comparativo STIB vs ${competitor.name}`}>
              <thead>
                <tr className="bg-secondary text-white">
                  <th className="text-left px-6 py-5 font-heading text-sm uppercase tracking-wider">Critério</th>
                  <th className="text-center px-4 py-5 font-heading text-sm uppercase tracking-wider">STIB</th>
                  <th className="text-center px-4 py-5 font-heading text-sm uppercase tracking-wider">{competitor.name}</th>
                  <th className="text-center px-4 py-5 font-heading text-sm uppercase tracking-wider">Vencedor</th>
                </tr>
              </thead>
              <tbody>
                {competitor.comparisonCategories.map((cat, i) => (
                  <tr key={i} className={`border-b border-gray-50 transition-colors hover:bg-gray-50/50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                    <td className="px-6 py-5">
                      <span className="font-body font-semibold text-secondary text-sm">{cat.label}</span>
                    </td>
                    <td className="px-4 py-5">
                      <p className="font-body text-sm text-gray-600 leading-snug">{cat.stibValue}</p>
                    </td>
                    <td className="px-4 py-5">
                      <p className="font-body text-sm text-gray-600 leading-snug">{cat.competitorValue}</p>
                    </td>
                    <td className="px-4 py-5 text-center">
                      {cat.winner === 'stib' && (
                        <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1.5 rounded-full font-body text-xs font-bold">
                          <i className="ri-trophy-line text-xs"></i> STIB
                        </span>
                      )}
                      {cat.winner === 'competitor' && (
                        <span className="inline-flex items-center gap-1 bg-secondary/10 text-secondary px-3 py-1.5 rounded-full font-body text-xs font-bold">
                          <i className="ri-trophy-line text-xs"></i> {competitor.name}
                        </span>
                      )}
                      {cat.winner === 'tie' && (
                        <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 px-3 py-1.5 rounded-full font-body text-xs font-bold">
                          <i className="ri-equalizer-line text-xs"></i> Empate
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-secondary/5">
                  <td className="px-6 py-5 font-body font-bold text-secondary text-sm">TOTAL</td>
                  <td className="px-4 py-5 text-center">
                    <span className="font-body font-bold text-primary text-lg">{stibTotal}</span>
                  </td>
                  <td className="px-4 py-5 text-center">
                    <span className="font-body font-bold text-secondary text-lg">{compTotal}</span>
                  </td>
                  <td className="px-4 py-5 text-center">
                    <span className={`font-body font-bold text-lg ${winner === 'stib' ? 'text-primary' : winner === 'competitor' ? 'text-secondary' : 'text-gray-500'}`}>
                      {winner === 'stib' ? 'STIB' : winner === 'competitor' ? competitor.name : 'Empate'}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </section>

      {/* ── VISUAL BARS ─────────────────────────────────────── */}
      <section ref={barsRef} className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Visualização Gráfica
            </span>
            <h2 className={`font-heading font-bold text-4xl md:text-5xl text-secondary lowercase transition-all duration-700 ${barsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              quem se destaca em cada área?
            </h2>
          </div>

          <div className={`space-y-8 transition-all duration-700 delay-200 ${barsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <ComparisonBar
              label="Especialização em Palestra Silenciosa"
              stibScore={stibScores.specialization}
              competitorScore={competitor.specializationScore}
              stibLabel="Especialista"
              competitorLabel="Geralista"
            />
            <ComparisonBar
              label="Cobertura Geográfica"
              stibScore={stibScores.geoCoverage}
              competitorScore={competitor.geoCoverageScore}
              stibLabel="9 cidades SP"
              competitorLabel={competitor.geoCoverage.split(' ').slice(0, 2).join(' ')}
            />
            <ComparisonBar
              label="Quantidade de Equipamentos"
              stibScore={stibScores.equipment}
              competitorScore={competitor.equipmentScore}
              stibLabel="5.000+ fones"
              competitorLabel={competitor.equipmentCount === 'Não divulgado' ? 'Não divulga' : competitor.equipmentCount}
            />
            <ComparisonBar
              label="Experiência no Mercado"
              stibScore={stibScores.experience}
              competitorScore={competitor.experienceScore}
              stibLabel={`${STIB_BASE.yearsInMarket} anos`}
              competitorLabel={`${competitor.yearsInMarket} anos`}
            />
            <ComparisonBar
              label="Custo-Benefício"
              stibScore={stibScores.price}
              competitorScore={competitor.priceScore}
              stibLabel="Competitivo"
              competitorLabel="Premium"
            />
            <ComparisonBar
              label="Tecnologia"
              stibScore={stibScores.tech}
              competitorScore={competitor.techScore}
              stibLabel="RF moderno"
              competitorLabel="Padrão mercado"
            />
            <ComparisonBar
              label="Atendimento ao Cliente"
              stibScore={stibScores.service}
              competitorScore={competitor.serviceScore}
              stibLabel="Presencial SP"
              competitorLabel="Personalizado"
            />
            <ComparisonBar
              label="Suporte Técnico"
              stibScore={stibScores.support}
              competitorScore={competitor.supportScore}
              stibLabel="9 cidades"
              competitorLabel="Foco regional"
            />
          </div>
        </div>
      </section>

      {/* ── PROS & CONS ──────────────────────────────────────── */}
      <section ref={prosRef} className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Análise Completa
            </span>
            <h2 className={`font-heading font-bold text-4xl md:text-5xl text-secondary lowercase transition-all duration-700 ${prosVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              prós, contras e veredito
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* STIB */}
            <div className={`bg-white rounded-3xl p-8 border border-gray-100 transition-all duration-700 ${prosVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h3 className="font-heading font-bold text-secondary text-xl mb-6 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10">
                  <i className="ri-shield-check-line text-primary text-lg"></i>
                </div>
                STIB — Quando escolher
              </h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-body font-semibold text-green-700 text-sm mb-3 flex items-center gap-2">
                    <i className="ri-arrow-up-line text-green-500"></i>
                    Vantagens
                  </h4>
                  <ul className="space-y-2">
                    {competitor.comparisonCategories
                      .filter((c) => c.winner === 'stib')
                      .map((c, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <i className="ri-check-line text-primary text-sm mt-0.5 flex-shrink-0"></i>
                          <span className="font-body text-sm text-gray-600">{c.label}: {c.stibValue}</span>
                        </li>
                      ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-body font-semibold text-secondary text-sm mb-3 flex items-center gap-2">
                    <i className="ri-user-smile-line text-secondary"></i>
                    Ideal para
                  </h4>
                  <ul className="space-y-2">
                    {competitor.idealFor.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <i className="ri-check-double-line text-primary text-sm mt-0.5 flex-shrink-0"></i>
                        <span className="font-body text-sm text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Competitor */}
            <div className={`bg-white rounded-3xl p-8 border border-gray-100 transition-all duration-700 delay-100 ${prosVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h3 className="font-heading font-bold text-secondary text-xl mb-6 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary/10">
                  <i className="ri-building-line text-secondary text-lg"></i>
                </div>
                {competitor.name} — Quando escolher
              </h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-body font-semibold text-green-700 text-sm mb-3 flex items-center gap-2">
                    <i className="ri-arrow-up-line text-green-500"></i>
                    Vantagens
                  </h4>
                  <ul className="space-y-2">
                    {competitor.comparisonCategories
                      .filter((c) => c.winner === 'competitor')
                      .map((c, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <i className="ri-check-line text-secondary text-sm mt-0.5 flex-shrink-0"></i>
                          <span className="font-body text-sm text-gray-600">{c.label}: {c.competitorValue}</span>
                        </li>
                      ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-body font-semibold text-secondary text-sm mb-3 flex items-center gap-2">
                    <i className="ri-user-smile-line text-secondary"></i>
                    Ideal para
                  </h4>
                  <ul className="space-y-2">
                    {competitor.idealFor.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <i className="ri-check-double-line text-secondary text-sm mt-0.5 flex-shrink-0"></i>
                        <span className="font-body text-sm text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Weaknesses */}
          <div className={`bg-red-50 rounded-3xl p-8 border border-red-100 transition-all duration-700 delay-200 max-w-2xl mx-auto ${prosVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h4 className="font-body font-semibold text-red-700 text-sm mb-4 flex items-center gap-2">
              <i className="ri-arrow-down-line text-red-500"></i>
              Limitações da {competitor.name}
            </h4>
            <ul className="space-y-2">
              {competitor.weaknesses.map((w, i) => (
                <li key={i} className="flex items-start gap-2">
                  <i className="ri-close-line text-red-400 text-sm mt-0.5 flex-shrink-0"></i>
                  <span className="font-body text-sm text-red-700/80">{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────── */}
      <section
        ref={faqRef}
        className="py-24 bg-white"
        aria-label={`Perguntas frequentes STIB vs ${competitor.name}`}
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Dúvidas Frequentes
            </span>
            <h2 className={`font-heading font-bold text-4xl md:text-5xl text-secondary lowercase transition-all duration-700 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              faq — stib vs {competitor.name.toLowerCase()}
            </h2>
          </div>

          <div className={`space-y-4 transition-all duration-700 delay-200 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {competitor.faqs.map((f, i) => (
              <FAQItem key={i} question={f.question} answer={f.answer} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── OTHER COMPARISONS ───────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-secondary lowercase">
              outros comparativos
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {getCompetitorBySlug && ALL_COMPETITORS.filter((c) => c.slug !== competitor.slug).map((c) => (
              <Link
                key={c.slug}
                to={`/comparativo/${c.slug}`}
                className="inline-flex items-center gap-2 bg-white border border-gray-200 px-6 py-3 rounded-full font-body text-sm text-secondary hover:border-primary hover:text-primary transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-scales-3-line text-primary text-sm"></i>
                STIB vs {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────── */}
      <section ref={ctaRef} className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-secondary to-secondary/90"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className={`font-heading font-bold text-4xl md:text-5xl text-white lowercase mb-6 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            ainda com dúvidas?
          </h2>
          <p className={`font-body text-lg text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed transition-all duration-700 delay-100 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            Fale diretamente com a STIB. Solicite um orçamento sem compromisso e compare preço, prazo e condições. Atendemos em 9 cidades de São Paulo e 11 capitais brasileiras.
          </p>
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-200 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <a
              href="/orcamento"
              className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              SOLICITAR ORÇAMENTO
              <i className="ri-arrow-right-line text-white text-lg"></i>
            </a>
            <a
              href={`https://wa.me/5511917776227?text=Olá! Vi o comparativo STIB vs ${competitor.name} e gostaria de um orçamento.`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-4 rounded-full font-body font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-green-400 text-xl"></i>
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}